/**
 * DesignDaySelector Component
 *
 * Main component for selecting design days from a weather file.
 * Groups design days by type and allows selection for IDF injection.
 */

import { DesignDayGroup } from './DesignDayGroup';
import type { DesignDayMetadata, ParsedDesignDay, DesignDayType } from '../../types';

export interface DesignDaySelectorProps {
  /** Design days grouped by type */
  groupedDesignDays: Record<DesignDayType, Array<DesignDayMetadata | ParsedDesignDay>>;
  /** Currently selected design day IDs */
  selectedIds: Set<string>;
  /** Recommended design day IDs */
  recommendedIds: string[];
  /** Whether data is loading */
  isLoading: boolean;
  /** Error message if loading failed */
  error: string | null;
  /** Callback when selection changes */
  onToggle: (id: string) => void;
  /** Callback to view design day details */
  onViewDetails: (designDay: DesignDayMetadata | ParsedDesignDay) => void;
  /** Callback to select recommended design days */
  onSelectRecommended: () => void;
  /** Callback to clear all selections */
  onClearAll: () => void;
  /** Callback to add selected design days to model */
  onAddToModel: () => void;
  /** Whether the "Add to Model" action is available */
  canAddToModel: boolean;
}

const TYPE_ORDER: DesignDayType[] = ['heating', 'cooling', 'extreme', 'dehumidification', 'humidification', 'other'];

export function DesignDaySelector({
  groupedDesignDays,
  selectedIds,
  recommendedIds,
  isLoading,
  error,
  onToggle,
  onViewDetails,
  onSelectRecommended,
  onClearAll,
  onAddToModel,
  canAddToModel,
}: DesignDaySelectorProps) {
  // Calculate totals
  const totalCount = TYPE_ORDER.reduce((sum, type) => sum + (groupedDesignDays[type]?.length ?? 0), 0);
  const selectedCount = selectedIds.size;

  // Loading state
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        <p className="mt-4">Loading design days...</p>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="48"
          height="48"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="text-destructive mb-4"
        >
          <circle cx="12" cy="12" r="10" />
          <path d="m15 9-6 6" />
          <path d="m9 9 6 6" />
        </svg>
        <p className="font-medium text-destructive">Failed to load design days</p>
        <p className="mt-1 text-sm text-muted-foreground">{error}</p>
      </div>
    );
  }

  // Empty state
  if (totalCount === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center text-muted-foreground">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="48"
          height="48"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="mb-4"
        >
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
        <p>No design days available for this weather file.</p>
        <p className="mt-1 text-sm">The DDY file may not be included in the archive.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with counts */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          {totalCount} design days available
        </div>
        {selectedCount > 0 && (
          <div className="text-sm font-medium text-primary">
            {selectedCount} selected
          </div>
        )}
      </div>

      {/* Design day groups */}
      <div className="space-y-3">
        {TYPE_ORDER.map((type) => {
          const days = groupedDesignDays[type];
          if (!days || days.length === 0) return null;
          return (
            <DesignDayGroup
              key={type}
              type={type}
              designDays={days}
              selectedIds={selectedIds}
              recommendedIds={recommendedIds}
              onToggle={onToggle}
              onViewDetails={onViewDetails}
            />
          );
        })}
      </div>

      {/* Action buttons */}
      <div className="flex flex-wrap items-center gap-2 border-t pt-4">
        <button
          type="button"
          onClick={onSelectRecommended}
          className="rounded border px-3 py-1.5 text-sm hover:bg-muted"
        >
          Select Recommended
        </button>
        <button
          type="button"
          onClick={onClearAll}
          className="rounded border px-3 py-1.5 text-sm hover:bg-muted"
          disabled={selectedCount === 0}
        >
          Clear All
        </button>

        <div className="flex-1" />

        {canAddToModel && (
          <button
            type="button"
            onClick={onAddToModel}
            disabled={selectedCount === 0}
            className="rounded bg-primary px-4 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Add to Model ({selectedCount})
          </button>
        )}
      </div>
    </div>
  );
}
